import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Select, message, Spin } from 'antd';
import type { Customer, CreateCustomerRequest } from '../../types/proposal';
import { proposalApi } from '../../services/proposal';

const { TextArea } = Input;

interface CustomerFormProps {
  open: boolean;
  customerId?: string;
  onClose: () => void;
  onSuccess: () => void;
}

// 行业选项
const industryOptions = [
  { value: '美妆护肤', label: '美妆护肤' },
  { value: '服饰时尚', label: '服饰时尚' },
  { value: '数码电子', label: '数码电子' },
  { value: '食品饮料', label: '食品饮料' },
  { value: '母婴用品', label: '母婴用品' },
  { value: '家居生活', label: '家居生活' },
  { value: '运动健身', label: '运动健身' },
  { value: '教育培训', label: '教育培训' },
  { value: '游戏娱乐', label: '游戏娱乐' },
  { value: '金融理财', label: '金融理财' },
  { value: '医疗健康', label: '医疗健康' },
  { value: '旅游出行', label: '旅游出行' },
  { value: '其他', label: '其他' },
];

export const CustomerForm: React.FC<CustomerFormProps> = ({
  open,
  customerId,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const isEdit = !!customerId;

  // 编辑模式下获取客户详情
  useEffect(() => {
    if (open && customerId) {
      fetchCustomerData(customerId);
    } else if (open) {
      form.resetFields();
    }
  }, [open, customerId]);

  const fetchCustomerData = async (id: string) => {
    setFetchLoading(true);
    try {
      const response = await proposalApi.getCustomer(id);
      const customer = response.data;
      form.setFieldsValue({
        company_name: customer.company_name,
        brand_name: customer.brand_name,
        industry: customer.industry,
        website: customer.website,
        contact_name: customer.contact_name,
        contact_email: customer.contact_email,
        contact_phone: customer.contact_phone,
        notes: customer.notes,
      });
    } catch (error) {
      message.error('获取客户信息失败');
      onClose();
    } finally {
      setFetchLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const data: CreateCustomerRequest = {
        company_name: values.company_name,
        brand_name: values.brand_name,
        industry: values.industry,
        website: values.website,
        contact_name: values.contact_name,
        contact_email: values.contact_email,
        contact_phone: values.contact_phone,
        notes: values.notes,
      };

      if (isEdit && customerId) {
        await proposalApi.updateCustomer(customerId, data);
        message.success('客户信息更新成功');
      } else {
        await proposalApi.createCustomer(data);
        message.success('客户创建成功');
      }

      form.resetFields();
      onSuccess();
      onClose();
    } catch (error) {
      if (!isEdit) {
        message.error('创建客户失败');
      } else {
        message.error('更新客户信息失败');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={isEdit ? '编辑客户' : '新建客户'}
      open={open}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={loading}
      width={600}
    >
      <Spin spinning={fetchLoading}>
        <Form
          form={form}
          layout="vertical"
        >
        <Form.Item
          name="company_name"
          label="公司名称"
          rules={[{ required: true, message: '请输入公司名称' }]}
        >
          <Input placeholder="请输入公司名称" maxLength={100} />
        </Form.Item>

        <Form.Item
          name="brand_name"
          label="品牌名称"
        >
          <Input placeholder="请输入品牌名称" maxLength={100} />
        </Form.Item>

        <Form.Item
          name="industry"
          label="所属行业"
        >
          <Select
            placeholder="请选择所属行业"
            allowClear
            options={industryOptions}
          />
        </Form.Item>

        <Form.Item
          name="website"
          label="公司网站"
        >
          <Input placeholder="请输入公司网站URL" maxLength={200} />
        </Form.Item>

        <Form.Item
          name="contact_name"
          label="联系人"
        >
          <Input placeholder="请输入联系人姓名" maxLength={50} />
        </Form.Item>

        <Form.Item
          name="contact_email"
          label="联系邮箱"
          rules={[
            {
              validator: (_: unknown, value: string) => {
                if (!value || /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value)) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('请输入有效的邮箱地址'));
              },
            },
          ]}
        >
          <Input placeholder="请输入联系邮箱" maxLength={100} />
        </Form.Item>

        <Form.Item
          name="contact_phone"
          label="联系电话"
        >
          <Input placeholder="请输入联系电话" maxLength={20} />
        </Form.Item>

        <Form.Item
          name="notes"
          label="备注说明"
        >
          <TextArea
            placeholder="请输入备注说明"
            rows={4}
            maxLength={500}
            showCount
          />
        </Form.Item>
      </Form>
    </Spin>
  </Modal>
  );
};
